import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { BaseDocument } from '@dato-proxy/entities/basedocument.entity';
import { BaseTransDocument } from '@dato-proxy/entities/basetransdocument.entity';
import { CompanyView } from '@dato-proxy/views/company.view';
import { BaseComponent } from '@m4u-angular/base/component/BaseComponent';
import { ServiceBagService, ServiceBagServices } from '@m4u-angular/base/component/ServiceBag';
import { BaseDataSourceAbstract } from '@m4u-angular/base/datalayer/BaseDataSourceAbstract';
import { BaseState } from '@m4u-angular/base/entities/BaseEntities';
import { DxListComponent } from 'devextreme-angular';
import dxCheckBox from 'devextreme/ui/check_box';
import { Globals } from 'projects/dato/_globals';

class DocumentListState extends BaseState {
    ReportItemsState: any = {};
    IsShowAdditionalNotes: boolean;
    IsActiveUser: boolean;
    IsAllowPayment: boolean;
    IsEnableMultiCompany: boolean;
    IsAllSelected: boolean;
}

@Component({
    selector: 'app-base-document-list',
    template: '<div></div>'
})
export class BaseDocumentListComponent extends BaseComponent<DocumentListState> {

    @Input() DataSource: BaseDataSourceAbstract;
    @Input() IsOpen: boolean;
    @Input() IsShowAttachments: boolean;
    @Input() Companies: CompanyView[];

    @Output() SelectionChanged: EventEmitter<BaseDocument[]> = new EventEmitter<BaseDocument[]>();
    @Output() PayByOne: EventEmitter<BaseDocument> = new EventEmitter<BaseDocument>();
    @Output() DocNumClick: EventEmitter<BaseDocument> = new EventEmitter<BaseDocument>();
    @Output() ShowFiles: EventEmitter<BaseDocument> = new EventEmitter<BaseDocument>();

    @ViewChild(DxListComponent) list: DxListComponent;

    constructor(A: ServiceBagService) {
        super(A, 'BaseDocumentListComponent', DocumentListState);
    }

    protected override OnInitState(): void {
        this.State.IsShowAdditionalNotes = Globals.SystemOption.IsShowAdditionalNotes;
        this.State.IsActiveUser = Globals.IsActiveUser;
        this.State.IsAllowPayment = Globals.SystemOption.IsAllowPayment;
        this.State.IsEnableMultiCompany = Globals.SystemOption.IsEnableMultiCompany;
    }

    ToggleReportItem(event: any, item: any) {
        this.State.ReportItemsState[item.EntryID] = !this.State.ReportItemsState[item.EntryID];

        if (event) {
            event.preventDefault();
            event.stopPropagation();

            const listItemElement = this.FindListItemElement(event.srcElement);
            if (this.State.ReportItemsState[item.EntryID]) listItemElement.classList.add('opened');
            else listItemElement.classList.remove('opened');
        }
    }

    private FindListItemElement(element: HTMLElement): HTMLElement {
        const parent = element.parentElement;
        if (parent.classList.contains('dx-list-item')) return parent;

        return this.FindListItemElement(parent);
    }

    OnContentReady(e) {
        this.list?.instance?.option('selectionByClick', false);
        this.DataSource && this.DataSource.GetItemsAsync().subscribe(documents => {
            if (this.State.IsAllowPayment) {
                // get documents that should not be checked
                const disabledDocuments = this.GetDisabledDocuments(documents);

                // get documents indexes in datasource
                const disabledDocumentIndexes = disabledDocuments.map(x => documents.indexOf(x));
                disabledDocumentIndexes.forEach(x => {
                    // disable checkbox for particular item
                    if (!this.list?.instance) return;

                    let itemElement = this.list.instance['getItemElementByFlatIndex'];
                    if (!itemElement) return;

                    // hotfix -> no context for js function
                    itemElement = itemElement.bind(this.list.instance);

                    if (!itemElement(x) || !itemElement(x).length) return;

                    const element = itemElement(x)[0] as HTMLElement;
                    if (!element) return;

                    const elements = element.getElementsByClassName('dx-checkbox');
                    if (!elements || !elements.length) return;

                    const checkbox = elements[0];
                    const instance = checkbox && dxCheckBox.getInstance(checkbox);
                    instance && instance.option("disabled", true);
                });
            }

            const documentsToHide = documents.filter(x => x.EntryID === 0 && x.OpenBalance === 0);
            documentsToHide.forEach(x => x.visible = false);
        });
    }

    OnSelectAllChanged(e) {
        if (!e.value) {
            this.list.instance.unselectAll();
            return;
        }

        this.DataSource && this.DataSource.GetItemsAsync().subscribe(documents => {
            // get documents that should be selected
            const documentsForSelection = this.GetEnabledDocuments(documents);
            documentsForSelection.forEach(x => this.list.instance.selectItem(x));
        });
    }

    OnSelectionChanged(e) {
        const documents = this.list.instance.option('selectedItems');

        // unselect needed items
        const disabledDocuments = this.GetDisabledDocuments(documents);
        disabledDocuments.forEach(x => this.list.instance.unselectItem(x));

        this.SelectionChanged.emit(documents);
    }

    private GetDisabledDocuments(documents: BaseDocument[]) {
        return documents.filter(x => this.IsDocumentDisabled(x));
    }

    private GetEnabledDocuments(documents: BaseDocument[]) {
        return documents.filter(x => !this.IsDocumentDisabled(x));
    }

    private IsDocumentDisabled(document: BaseDocument) {
        return !document.PaymentSum || document.PaymentSum === 0 || (document as BaseTransDocument).IsPaymentAllowed == false;
    }

    IsDocNumDisabled = (item) => {
        //return !item || !item.PdfUrl;
        return !item || (!this.IsAllowedToViewUnsent(item.TransType) && !item.PdfUrl);
    }
    private IsAllowedToViewUnsent(transType: number): boolean {
        switch (transType) {
            case (13): case (14): {
                return Globals.SystemOption.IsAllowViewingUnsentInvoices;
            }
            case (15): case (16): {
                return Globals.SystemOption.IsAllowViewingUnsentDeliveries;
            }
            case (24): {
                return Globals.SystemOption.IsAllowViewingUnsentReceipt;
            }
            default: { return false }
        }
    }

    OnDocNumClick(item: BaseDocument) {
        item && this.DocNumClick.emit(item);
    }

    OnPay(Document: BaseDocument) {
        this.list.instance.unselectAll();
        this.PayByOne.emit(Document);
    }

    OnShowFiles(e, document: BaseDocument) {
        e?.preventDefault();
        e?.stopPropagation();

        this.ShowFiles.emit(document);
    }
}
